/**
 * UploadSOPDialog
 * Dialog for manually uploading a SOP from a markdown/text file.
 * Creates a normal SOP (draft / pending) via POST /sops.
 */

import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Upload, FileText } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useUploadSOP } from '@/hooks';
import { getApiErrorMessage } from '@/lib/utils';
import { cn } from '@/lib/utils';
import { CategoryCombobox } from './category-combobox';

const ALLOWED_FILE_TYPES = ['.md', '.txt'];

const uploadSOPSchema = z.object({
  title: z.string().min(1, 'Title is required').max(200, 'Title is too long'),
  category: z.string().min(1, 'Category is required'),
  subcategory: z.string().min(1, 'Subcategory is required'),
  version: z.string().optional(),
  content: z.string().min(1, 'Upload a .md or .txt file to load content')
});

type UploadSOPFormData = z.infer<typeof uploadSOPSchema>;

interface UploadSOPDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Only the names are read — the Pulse tree or the category tree both fit. */
  categories: { category: string; subcategories: { name: string }[] }[];
  /** After a successful upload — the draft exists. */
  onUploaded?: () => void;
}

const DEFAULT_VALUES: UploadSOPFormData = {
  title: '',
  category: '',
  subcategory: '',
  version: '1.0',
  content: ''
};

export function UploadSOPDialog({ open, onOpenChange, categories, onUploaded }: UploadSOPDialogProps) {
  const uploadMutation = useUploadSOP();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState('');

  const form = useForm<UploadSOPFormData>({
    resolver: zodResolver(uploadSOPSchema),
    defaultValues: DEFAULT_VALUES
  });

  useEffect(() => {
    if (open) {
      form.reset(DEFAULT_VALUES);
      setFileName('');
    }
  }, [open, form]);

  const selectedCategory = form.watch('category');
  const categoryOptions = Array.from(new Set(categories.map((c) => c.category))).sort();
  const matchedCategory = categories.find((c) => c.category === selectedCategory);
  const subcategoryOptions = Array.from(
    new Set((matchedCategory?.subcategories ?? categories.flatMap((c) => c.subcategories)).map((s) => s.name))
  ).sort();

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    const extension = '.' + (file.name.split('.').pop()?.toLowerCase() ?? '');
    if (!ALLOWED_FILE_TYPES.includes(extension)) {
      toast.error(`Unsupported file format. Only ${ALLOWED_FILE_TYPES.join(', ')} are supported.`);
      return;
    }
    try {
      const text = await file.text();
      setFileName(file.name);
      form.setValue('content', text, { shouldValidate: true });
      if (!form.getValues('title').trim()) {
        form.setValue('title', file.name.replace(/\.[^.]+$/, ''), { shouldValidate: true });
      }
    } catch {
      toast.error('Failed to read file');
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFile(e.target.files?.[0]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const onSubmit = async (data: UploadSOPFormData) => {
    try {
      await uploadMutation.mutateAsync({
        title: data.title,
        category: data.category,
        subcategory: data.subcategory,
        content: data.content,
        version: data.version?.trim() || undefined
      });
      toast.success('Uploaded — it is a draft to review on the Protocols page');
      onOpenChange(false);
      onUploaded?.();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to upload'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='w-lg p-4'>
        <DialogHeader>
          <DialogTitle className='text-sm font-semibold'>Upload for a protocol</DialogTitle>
          <DialogDescription className='text-xs'>
            Upload a markdown or text file. It becomes a draft to review; publishing it builds a protocol with the same
            structure as one drafted from tickets.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className='flex flex-col gap-4'>
            {/* File picker */}
            <div
              className={cn(
                'flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-muted-foreground/25 py-5 cursor-pointer transition-colors hover:border-primary/50',
                fileName && 'border-primary/40 bg-primary/5'
              )}
              onClick={() => fileInputRef.current?.click()}
            >
              {fileName ? (
                <>
                  <FileText className='size-6 text-primary' />
                  <p className='text-xs font-medium'>{fileName}</p>
                  <p className='text-[10px] text-muted-foreground'>Click to choose a different file</p>
                </>
              ) : (
                <>
                  <Upload className='size-6 text-muted-foreground' />
                  <p className='text-xs font-medium'>Choose a .md or .txt file</p>
                </>
              )}
              <input
                ref={fileInputRef}
                type='file'
                accept={ALLOWED_FILE_TYPES.join(',')}
                onChange={handleFileInputChange}
                className='hidden'
              />
            </div>
            <FormField
              control={form.control}
              name='content'
              render={() => (
                <FormItem className='-mt-2'>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='title'
              render={({ field }) => (
                <FormItem>
                  <FormLabel className='text-xs font-medium text-gray-700'>Title</FormLabel>
                  <FormControl>
                    <Input className='text-xs h-10' placeholder='Enter protocol title' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className='flex gap-3'>
              <FormField
                control={form.control}
                name='category'
                render={({ field }) => (
                  <FormItem className='flex-1'>
                    <FormLabel className='text-xs font-medium text-gray-700'>Category</FormLabel>
                    <FormControl>
                      <CategoryCombobox
                        value={field.value}
                        onChange={field.onChange}
                        options={categoryOptions}
                        placeholder='Select or add'
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='subcategory'
                render={({ field }) => (
                  <FormItem className='flex-1'>
                    <FormLabel className='text-xs font-medium text-gray-700'>Subcategory</FormLabel>
                    <FormControl>
                      <CategoryCombobox
                        value={field.value}
                        onChange={field.onChange}
                        options={subcategoryOptions}
                        placeholder='Select or add'
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name='version'
              render={({ field }) => (
                <FormItem className='w-32'>
                  <FormLabel className='text-xs font-medium text-gray-700'>Version</FormLabel>
                  <FormControl>
                    <Input className='text-xs h-10' placeholder='1.0' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className='mt-2'>
              <Button type='button' variant='outline' size='sm' className='text-xs' onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type='submit' size='sm' className='text-xs' disabled={uploadMutation.isPending}>
                {uploadMutation.isPending ? 'Uploading…' : 'Upload'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
